'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { AudioLines, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getComposerAffordanceDelays } from './composer-affordance-motion';
import type { MarketingPageComposer } from './marketing-page-content-types';
import { SectionShell } from './section-shell';

export function LandingComposer({
  composer,
}: {
  composer: MarketingPageComposer;
}) {
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  const [mobileUseCasesExpanded, setMobileUseCasesExpanded] = useState(false);
  const leftAffordanceRef = useRef<HTMLDivElement>(null);
  const rightAffordanceRef = useRef<HTMLDivElement>(null);
  const hasStartedAffordanceAnimation = useRef(false);

  useLayoutEffect(() => {
    if (hasStartedAffordanceAnimation.current) return;

    const leftAffordance = leftAffordanceRef.current;
    const rightAffordance = rightAffordanceRef.current;
    if (!leftAffordance || !rightAffordance) return;

    hasStartedAffordanceAnimation.current = true;
    const delays = getComposerAffordanceDelays();

    leftAffordance.style.animationDelay = `${delays.left}ms`;
    leftAffordance.classList.remove('md:translate-x-30');
    leftAffordance.classList.add('md:animate-marketing-composer-left');

    rightAffordance.style.animationDelay = `${delays.right}ms`;
    rightAffordance.classList.remove('md:-translate-x-30');
    rightAffordance.classList.add('md:animate-marketing-composer-right');
  }, []);

  return (
    <section className="bg-marketing-bg w-full shrink-0 pb-3 md:pb-24">
      <SectionShell>
        <div className="bg-marketing-panel flex w-full flex-col items-center rounded-lg p-1 md:rounded-4xl md:px-8 md:py-20 lg:py-24">
          <Link
            href={composer.href}
            aria-label={composer.accessibleLabel}
            className="focus-visible:outline-marketing-accent-strong mx-auto flex w-full max-w-250 shrink-0 flex-wrap items-stretch gap-1 outline-none focus-visible:outline-2 focus-visible:outline-offset-4 md:flex-nowrap md:gap-0.5"
          >
            <ComposerAffordance
              elementRef={leftAffordanceRef}
              className="order-2 flex-1 md:order-1 md:size-28 md:flex-none md:translate-x-30 md:motion-reduce:translate-x-0 md:motion-reduce:animate-none"
              label={composer.addLabel}
            >
              <Plus className="size-5" aria-hidden="true" />
              <div
                aria-hidden="true"
                className="bg-marketing-field absolute top-1/2 right-0 hidden h-14 w-0.5 translate-x-full -translate-y-1/2 md:block"
              />
            </ComposerAffordance>

            <div
              aria-live="polite"
              className={cn(
                'bg-marketing-field relative z-10 order-1 flex min-h-20 w-full cursor-text items-center justify-center rounded-md px-6 py-5 text-center text-sm leading-[1.35] [letter-spacing:-0.015em] md:rounded-2xl md:text-lg',
                'md:order-2 md:w-auto md:flex-1',
                selectedExample
                  ? 'text-marketing-field-fg'
                  : 'text-marketing-fg-muted'
              )}
            >
              {selectedExample ?? composer.placeholder}
            </div>

            <ComposerAffordance
              elementRef={rightAffordanceRef}
              className="order-3 flex-1 md:size-28 md:flex-none md:-translate-x-30 md:motion-reduce:translate-x-0 md:motion-reduce:animate-none"
              label={composer.voiceLabel}
            >
              <div
                aria-hidden="true"
                className="bg-marketing-field absolute top-1/2 left-0 hidden h-14 w-0.5 -translate-x-full -translate-y-1/2 md:block"
              />
              <AudioLines className="size-5" aria-hidden="true" />
            </ComposerAffordance>
          </Link>

          <button
            type="button"
            id="marketing-composer-examples"
            aria-controls="marketing-composer-example-list"
            aria-expanded={mobileUseCasesExpanded}
            onClick={() => setMobileUseCasesExpanded((expanded) => !expanded)}
            className={cn(
              'focus-visible:outline-marketing-accent-strong mt-1 flex w-full shrink-0 cursor-pointer items-center justify-center rounded-md text-sm font-medium text-white transition-[min-height,background-color] duration-500 ease-out outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 motion-reduce:transition-none md:hidden',
              mobileUseCasesExpanded ? 'min-h-10 bg-neutral-500/70' : 'min-h-20'
            )}
          >
            {composer.useCasesLabel}
          </button>

          <p className="mx-auto mt-8 hidden w-full text-center text-sm font-normal text-white md:block">
            {composer.useCasesLabel}
          </p>

          <ul
            id="marketing-composer-example-list"
            aria-labelledby="marketing-composer-examples"
            className={cn(
              'mx-auto flex min-h-0 w-full flex-1 flex-col justify-center gap-1 overflow-y-auto transition-[max-height,opacity,transform,margin] duration-500 ease-out motion-reduce:transition-none md:mt-4 md:max-h-none md:max-w-194.25 md:translate-y-0 md:flex-row md:flex-wrap md:gap-2 md:overflow-visible md:opacity-100 md:transition-none',
              mobileUseCasesExpanded
                ? 'mt-1 max-h-96 translate-y-0 opacity-100'
                : 'pointer-events-none max-h-0 translate-y-2 overflow-hidden opacity-0 md:pointer-events-auto'
            )}
          >
            {composer.examples.map((example) => {
              const isActive = selectedExample === example;

              return (
                <li key={example} className="w-full md:w-auto">
                  <button
                    type="button"
                    aria-pressed={isActive}
                    onClick={() =>
                      setSelectedExample((current) =>
                        current === example ? null : example
                      )
                    }
                    className={cn(
                      'focus-visible:outline-marketing-accent-strong min-h-9 w-full cursor-pointer rounded-sm px-2.5 py-1 text-xs leading-[1.45] font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 md:min-h-0 md:w-auto',
                      isActive
                        ? 'bg-marketing-accent-strong text-marketing-accent-fg'
                        : 'bg-neutral-600/50 text-white hover:bg-neutral-500/50'
                    )}
                  >
                    {example}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </SectionShell>
    </section>
  );
}

function ComposerAffordance({
  children,
  className,
  elementRef,
  label,
}: {
  children: React.ReactNode;
  className?: string;
  elementRef?: React.Ref<HTMLDivElement>;
  label: string;
}) {
  return (
    <div
      ref={elementRef}
      className={cn(
        'bg-marketing-chip md:bg-marketing-field relative flex min-h-20 items-center justify-center rounded-md md:rounded-2xl',
        className
      )}
    >
      <span className="sr-only">{label}</span>
      <div className="bg-marketing-chip text-marketing-chip-fg flex size-11 items-center justify-center rounded-xl">
        {children}
      </div>
    </div>
  );
}
